import React from 'react';
import { motion } from 'framer-motion';
import { Layers, ArrowRight, Users, Building2, Crown, Globe } from 'lucide-react';
export function NCJIPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1287" data-mp="1pywwfup125hy">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1288" data-mp="1q0zxd9rpxj1g">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1289" data-mp="1v045ry6tjcqq">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1290" data-mp="176uemg0ngj64">
            <span className="w-8 h-px bg-teal" data-id="element-1291" data-mp="1c7s65j7jupka"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1292" data-mp="1c5ffmhnq76vm">
              Neurocognitive Civic Judgment Index
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1293" data-mp="2nlm86w9j7wh0"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1294" data-mp="1b9xzymou8pqu">
            A Lifecycle Framework for Human Capability, Civic Development, and
            Leadership Readiness
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-1295" data-mp="127yi5s601hmu">
            The Neurocognitive Civic Judgment Index (NCJI) expands the SyNeural
            platform into a broader developmental architecture that helps
            translate capability gaps into growth pathways, support systems,
            institutional readiness, and leadership qualification across the
            full spectrum of social responsibility.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1296" data-mp="ehug3p8glpxy">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1297" data-mp="9jj87k1q5pid">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1298" data-mp="3w47admhyobxo">
            Where CJI provides the analytical foundation, NCJI expands that
            foundation into a full-spectrum system for human opportunity,
            lifecycle development, institutional readiness, civic resilience,
            and leadership qualification.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1299" data-mp="io9czgit83pv">
            It is designed not merely to identify limitations, but to convert
            them into pathways. Every meaningful weakness should be paired with
            a development route. Every barrier should be translated into a plan.
            Every plan should connect to some combination of self-directed
            action, institutional support, community reinforcement, or
            public-sector infrastructure.
          </p>
        </motion.div>
      </section>

      {/* From Assessment to Advancement */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-1300" data-mp="2h5tcc8signvj">
        <div className="max-w-5xl mx-auto" data-id="element-1301" data-mp="2h3p81pqp9muq">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1302" data-mp="2vteg84x7mr4i">
            <div className="flex items-center gap-4 mb-8" data-id="element-1303" data-mp="1y8s3gcvu63mb">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1304" data-mp="2d4pxnpm78xvw">
                <ArrowRight size={24} data-id="element-1305" data-mp="2842ajf13d16y" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1306" data-mp="3oiv54slmfpvz">
                From Assessment to Advancement
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed mb-10" data-id="element-1307" data-mp="3jjqstt3ehc1f">
              NCJI operates on a simple progression model that allows the
              framework to identify capability gaps, clarify what they mean,
              recommend concrete interventions, connect individuals or
              institutions to support structures, track progress over time, and
              unlock wider opportunity as readiness improves.
            </p>

            {/* Process Flow */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-1308" data-mp="2c19ajfe5purc">
              {[{
              step: 'Assess',
              desc: 'Identify gaps'
            }, {
              step: 'Explain',
              desc: 'Clarify meaning'
            }, {
              step: 'Recommend',
              desc: 'Concrete steps'
            }, {
              step: 'Support',
              desc: 'Connect resources'
            }, {
              step: 'Reassess',
              desc: 'Track progress'
            }, {
              step: 'Advance',
              desc: 'Unlock opportunity'
            }].map((item, index) => <motion.div key={item.step} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.1
            }} className="text-center" data-id="element-1309" data-mp="2vvir2eq8q6km" data-mpi={index}>
                  <div className="w-12 h-12 rounded-full bg-teal/10 border-2 border-teal mx-auto mb-3 flex items-center justify-center" data-id="element-1310" data-mp="f7olpthmg4gs">
                    <span className="text-sm font-bold text-teal" data-id="element-1311" data-mp="aebztr4bubrq">
                      {index + 1}
                    </span>
                  </div>
                  <h3 className="font-heading font-bold text-ink-dark text-sm mb-1" data-id="element-1312" data-mp="29m2aj1x6gba6">
                    {item.step}
                  </h3>
                  <p className="text-xs text-ink-muted" data-id="element-1313" data-mp="1uwd4asa89h31">{item.desc}</p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Human Opportunity and Development */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-teal-pale" data-id="element-1314" data-mp="1qx5diqaeb4o9">
        <div className="max-w-4xl mx-auto" data-id="element-1315" data-mp="1qu9f70umydza">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1316" data-mp="1c04sf5mpm7yr">
            <div className="flex items-center gap-4 mb-6" data-id="element-1317" data-mp="3bad0pt9v6olj">
              <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1318" data-mp="zq5hka8weqka">
                <Users size={24} data-id="element-1319" data-mp="urc24ca1wphh" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1320" data-mp="19ex5866xeope">
                Human Opportunity and Development
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1321" data-mp="14mm1stcz8ks3">
              A defining feature of NCJI is its Human Opportunity and
              Development framework. The system is designed to increase
              self-awareness, guide individuals toward practical pathways for
              success, and strengthen communities through better alignment of
              talent, support, and resources. Rather than freezing people inside
              static categories, the framework is meant to identify practical
              growth opportunities and make them actionable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Lifecycle Evolution */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1322" data-mp="33c49bdz02tbb">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1323" data-mp="38b73229j5d3z">
          <div className="flex items-center gap-4 mb-6" data-id="element-1324" data-mp="2kf32ropssl59">
            <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1325" data-mp="2pd4ko6bhmef5">
              <Layers size={24} data-id="element-1326" data-mp="2uch5ymmpndfm" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1327" data-mp="2uaml18bfvlbj">
              A Platform That Evolves With Responsibility
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed mb-8" data-id="element-1328" data-mp="9mrriapx5tng">
            NCJI is structured as a full-spectrum lifecycle framework:
          </p>

          <div className="space-y-6" data-id="element-1329" data-mp="en5yrqjp4zkf">
            {[{
            stage: 'Early Development',
            focus: 'Emotional regulation, cooperation, self-direction, and foundational reasoning'
          }, {
            stage: 'Education',
            focus: 'Logic, media literacy, planning, and consequential thinking'
          }, {
            stage: 'Adulthood',
            focus: 'Readiness and opportunity-alignment for workforce, entrepreneurship, civic participation, and public service'
          }, {
            stage: 'Institutional & Executive',
            focus: 'Leadership development, civil-service modernization, and high-responsibility decision environments'
          }].map((item, index) => <motion.div key={item.stage} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white p-6 rounded-lg border border-line" data-id="element-1330" data-mp="2vtd2ez3r252m" data-mpi={index}>
                <h3 className="text-lg font-heading font-bold text-ink-dark mb-2" data-id="element-1331" data-mp="3nwqgbnn3ojx9">
                  {item.stage}
                </h3>
                <p className="text-ink-muted leading-relaxed" data-id="element-1332" data-mp="3iv10luoxni2u">{item.focus}</p>
              </motion.div>)}
          </div>
        </motion.div>
      </section>

      {/* Core Capability Domains */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-1333" data-mp="3rvpij0hg62l0">
        <div className="max-w-5xl mx-auto" data-id="element-1334" data-mp="345le4qwfpysy">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1335" data-mp="305b7rd4qd6dk">
            <h2 className="text-3xl font-heading font-bold text-ink-dark mb-6 text-center" data-id="element-1336" data-mp="2g25ast76i8uk">
              Core Capability Domains
            </h2>
            <p className="text-lg text-ink-muted leading-relaxed mb-10 text-center max-w-3xl mx-auto" data-id="element-1337" data-mp="38snloge59eij">
              NCJI is built around foundational human capabilities that shape
              readiness across the lifecycle:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1338" data-mp="2yuhno9vgksiy">
              {[{
              name: 'Reasoning quality',
              link: '/reasoning-quality'
            }, {
              name: 'Consequential thinking',
              link: '/consequential-thinking'
            }, {
              name: 'Manipulation resistance',
              link: '/manipulation-resistance'
            }, {
              name: 'Emotional regulation',
              link: '/emotional-regulation'
            }, {
              name: 'Social cooperation',
              link: '/social-cooperation'
            }, {
              name: 'Self-direction and execution',
              link: '/self-direction'
            }, {
              name: 'Leadership responsibility',
              link: '/leadership-responsibility'
            }].map((domain, index) => <motion.a key={domain.name} href={domain.link} initial={{
              opacity: 0,
              scale: 0.95
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.05
            }} className="bg-white p-5 rounded-lg border border-line flex items-center gap-3 hover:border-navy hover:shadow-md transition-all group" data-id="element-1339" data-mp="2xfewp9daqf2d" data-mpi={index}>
                  <div className="w-2 h-2 rounded-full bg-navy flex-shrink-0" data-id="element-1340" data-mp="k8yucygeb82y"></div>
                  <span className="text-ink-dark font-medium group-hover:text-navy transition-colors" data-id="element-1341" data-mp="p67ciz1jnc47">
                    {domain.name}
                  </span>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-navy" data-id="element-1342" data-mp="z4ep75osv80e">
                    <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1343" data-mp="284wwdsv3f6je" />
                  </svg>
                </motion.a>)}
            </div>

            <p className="text-lg text-ink-muted leading-relaxed mt-10 text-center" data-id="element-1344" data-mp="2r396phxqqmgk">
              These domains create a common framework for understanding how
              individuals develop, how institutions strengthen, and how
              leadership can be evaluated more rigorously over time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Built For */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1345" data-mp="3ar8hutw7vvmi">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1346" data-mp="3apdwjeh1a1hw">
          <div className="flex items-center gap-4 mb-8 justify-center" data-id="element-1347" data-mp="3tdfzjjqwo5s1">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1348" data-mp="3ffreaasxewmg">
              <Building2 size={24} data-id="element-1349" data-mp="3kh71a8enzd7h" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1350" data-mp="35py0r49847fk">
              Built for Individuals, Institutions, and Nations
            </h2>
          </div>

          <p className="text-lg text-ink-muted leading-relaxed mb-10 text-center max-w-3xl mx-auto" data-id="element-1351" data-mp="2wphzm1fydcdv">
            NCJI is designed for use across multiple scales and environments:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1352" data-mp="2mrc01qhmcic6">
            {[{
            icon: <Users size={20} data-id="element-1353" data-mp="2rq5htv3xuvi5" />,
            title: 'Individual Level',
            desc: 'Human development, self-awareness, and opportunity guidance',
            link: '/individual-level'
          }, {
            icon: <Building2 size={20} data-id="element-1354" data-mp="23tiltfnb37ew" />,
            title: 'Organizational Level',
            desc: 'Readiness and capability-building inside institutions',
            link: '/organizational-level'
          }, {
            icon: <Globe size={20} data-id="element-1355" data-mp="2rkeai3wgtmhx" />,
            title: 'Municipal Scale',
            desc: 'Local civic resilience and community strengthening',
            link: '/municipal-scale'
          }, {
            icon: <Crown size={20} data-id="element-1356" data-mp="2hlgksxnazmxc" />,
            title: 'National Scale',
            desc: 'Strategic planning for civic resilience, institutional strengthening, and leadership qualification',
            link: '/national-scale'
          }].map((item, index) => <motion.a key={item.title} href={item.link} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white p-6 rounded-lg border border-line hover:border-navy hover:shadow-md transition-all group" data-id="element-1357" data-mp="1176y07ypm68m" data-mpi={index}>
                <div className="flex items-center gap-3 mb-3" data-id="element-1358" data-mp="4hv4ujsz8atz">
                  <div className="w-10 h-10 rounded-md bg-surface-muted flex items-center justify-center text-navy-light group-hover:bg-navy group-hover:text-white transition-colors" data-id="element-1359" data-mp="9fwp38252g71">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-heading font-bold text-ink-dark group-hover:text-navy transition-colors" data-id="element-1360" data-mp="2b8cijydyiefu">
                    {item.title}
                  </h3>
                </div>
                <p className="text-ink-muted leading-relaxed mb-3" data-id="element-1361" data-mp="lve51qas0zrn">
                  {item.desc}
                </p>
                <div className="flex items-center gap-1 text-navy text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1362" data-mp="vnags45scoec">
                  Learn More
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1363" data-mp="qoz65r7o29v2">
                    <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1364" data-mp="cwj0eindrxqg" />
                  </svg>
                </div>
              </motion.a>)}
          </div>
        </motion.div>
      </section>

      {/* NCJI Within SyNeural */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1365" data-mp="1qw48ce2mfgbm">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-navy-deep text-white p-12 rounded-2xl" data-id="element-1366" data-mp="1vv77jhvx6sbo">
          <h2 className="text-3xl font-heading font-bold mb-6" data-id="element-1367" data-mp="29oyecii9ny3p">
            NCJI Within the SyNeural Ecosystem
          </h2>
          <p className="text-lg leading-relaxed opacity-90" data-id="element-1368" data-mp="20utgvg8mnof7">
            NCJI is the broader architecture built on top of CJI. Where CJI
            provides the analytical and diagnostic foundation, NCJI expands the
            system into lifecycle development, opportunity guidance,
            institutional readiness, and executive-level leadership evaluation.
            Together, CJI and NCJI form a unified platform for measuring,
            developing, and scaling judgment across the full human and
            institutional spectrum.
          </p>
        </motion.div>
      </section>
    </div>;
}